import axios from 'axios'

const DEFAULT_COLOR = '#0060EE'

const PALETTE = [
  '#0060EE',
  '#0291FF',
  '#00BFFF',
  '#0B42B0',
  '#292F4D',
  '#4CDA63',
  '#FCB900',
  '#FF6900',
  '#E53935',
  '#EB144C',
  '#8509F6',
  '#9900EF'
]

const getCachedColor = () => {
  try {
    const c = localStorage.getItem('ib-primary-color')
    if (c && /^#[0-9A-Fa-f]{6}$/.test(c)) return c.toUpperCase()
  } catch (e) {}

  return null
}

const getCachedLogo = () => {
  try {
    const l = localStorage.getItem('ib-logo')
    if (l && typeof l === 'string' && l.length > 0) return l
  } catch (e) {}

  return null
}

const getDefaultState = () => ({
  primaryColor: getCachedColor(),
  previewColor: null,
  logo: getCachedLogo(),
  whitelabelEnabled: process.env.VUE_APP_WHITELABEL_ENABLED === 'true',
  timezone: 'UTC',
  palette: PALETTE,
  loaded: false
})

const state = getDefaultState()

const getters = {
  effectiveColor: (state) => {
    if (state.previewColor) return state.previewColor
    if (state.primaryColor) return state.primaryColor

    return DEFAULT_COLOR
  },
  defaultColor: () => DEFAULT_COLOR,
  palette: state => state.palette,
  whitelabelEnabled: state => state.whitelabelEnabled,
  timezone: state => state.timezone,
  hasUnsavedPreview: state => state.previewColor !== null && state.previewColor !== state.primaryColor,
  effectiveLogo: state => state.logo || null,
  hasCustomLogo: state => !!state.logo,
  canEditBranding: (state, getters, rootState, rootGetters) => {
    return state.whitelabelEnabled && (
      rootGetters['user/isTemplateAdmin'] ||
      rootGetters['user/isWhitelabelAdmin']
    )
  }
}

const mutations = {
  resetState (state) {
    Object.assign(state, getDefaultState())
  },
  setPrimaryColor (state, color) {
    state.primaryColor = color
    try {
      if (color) localStorage.setItem('ib-primary-color', color)
      else localStorage.removeItem('ib-primary-color')
    } catch (e) {}
  },
  setWhitelabelEnabled (state, value) {
    state.whitelabelEnabled = !!value
  },
  setTimezone (state, value) {
    state.timezone = value || 'UTC'
  },
  setLogo (state, logo) {
    state.logo = logo || null
    try {
      if (logo) localStorage.setItem('ib-logo', logo)
      else localStorage.removeItem('ib-logo')
    } catch (e) {}
  },
  setPreviewColor (state, color) {
    state.previewColor = color
  },
  clearPreview (state) {
    state.previewColor = null
  },
  setLoaded (state, value) {
    state.loaded = !!value
  }
}

const actions = {
  applyColorToDom (_, color) {
    document.documentElement.style.setProperty('--color-primary', color || DEFAULT_COLOR)
  },

  async fetchAppSettings ({ commit, dispatch }) {
    try {
      const response = await axios.get('app-settings')
      const data = response.data.payload.appSettings || {}
      commit('setPrimaryColor', data.primaryColor || null)
      commit('setWhitelabelEnabled', data.whitelabelEnabled)
      commit('setTimezone', data.timezone)
      commit('setLogo', data.logo || null)
      dispatch('applyColorToDom', data.primaryColor || DEFAULT_COLOR)
    } catch (e) {
      // Network error / endpoint missing → keep defaults, don't block app boot
    } finally {
      commit('setLoaded', true)
    }
  },

  async uploadLogo ({ commit }, file) {
    const formData = new FormData()
    formData.append('file', file)
    const response = await axios.post('admin/app-settings/logo', formData)
    const data = response.data.payload.appSettings || {}
    commit('setLogo', data.logo || null)

    return response
  },

  async removeLogo ({ commit }) {
    const response = await axios.delete('admin/app-settings/logo')
    const data = response.data.payload.appSettings || {}
    commit('setLogo', data.logo || null)

    return response
  },

  async saveColor ({ commit, dispatch }, color) {
    const response = await axios.put('admin/app-settings', { primaryColor: color })
    const data = response.data.payload.appSettings || {}
    commit('setPrimaryColor', data.primaryColor || null)
    commit('clearPreview')
    dispatch('applyColorToDom', data.primaryColor || DEFAULT_COLOR)

    return response
  },

  resetToDefault ({ dispatch }) {
    return dispatch('saveColor', null)
  },

  startPreview ({ commit, dispatch }, color) {
    commit('setPreviewColor', color)
    dispatch('applyColorToDom', color)
  },

  cancelPreview ({ commit, dispatch, state }) {
    commit('clearPreview')
    dispatch('applyColorToDom', state.primaryColor || DEFAULT_COLOR)
  }
}

export default {
  namespaced: true,
  state,
  getters,
  mutations,
  actions
}
